import { Component, Prop, Vue } from 'vue-property-decorator';
import ModalComponent from '@/public/components/modal-component/modal-component.vue';
import { ApiClient } from '@/shared/api-client';

export type SpeakerRequestDialogMode = 'AgendaItemSpeech' | 'OrderQuestion';

@Component({
    components: {
        ModalComponent
    }
})
export default class SpeakerRequestDialogComponent extends Vue {

    @Prop() public activityId!: string;
    @Prop({ default: () => ({}) }) public translations!: { [key: string]: string };

    public proposal = '';
    public isSubmitting = false;
    public errorMessage: string | null = null;

    private apiClient: ApiClient = new ApiClient();
    private mode: SpeakerRequestDialogMode = "AgendaItemSpeech";
    private agendaItemId: string | undefined = undefined;

    public get modalName(): string {
        return `speakerRequestDialog_${this.mode}_${this.agendaItemId || 'noAgendaItem'}`;
    }

    public get showProposal(): boolean {
        return this.mode === 'AgendaItemSpeech';
    }

    public t(key: string): string {
        return this.translations?.[key] ?? '';
    }

    public get title(): string {
        return this.mode === 'OrderQuestion'
            ? this.t('speakerRequest_title_orderQuestion')
            : this.t('speakerRequest_title_agendaItemSpeech');
    }

    public get description(): string {

        if (this.mode === 'OrderQuestion') {
            return this.t('speakerRequest_description_orderQuestion');
        }

        return ``;
    }

    private resetForm(): void {
        this.proposal = '';
        this.isSubmitting = false;
        this.errorMessage = null;
    }

    public mounted(): void {
        // Move the modal element to <body> so it is not trapped inside a parent modal's
        // stacking context (e.g. when opened from agenda-hierarchy-item-component).
        document.body.appendChild(this.$el);
    }

    public beforeDestroy(): void {
        if (this.$el.parentNode === document.body) {
            document.body.removeChild(this.$el);
        }
    }

    public open(mode: SpeakerRequestDialogMode, agendaItemId?: string): void {

        this.mode = mode;
        this.agendaItemId = agendaItemId;

        // Wait for Vue to update the DOM (modal id attribute) before showing via jQuery
        this.$nextTick(() => {
            // bootstrap modal
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).$(`#${this.modalName}`).modal('show');
            this.resetForm();
        });
    }

    public async submit(): Promise<void> {

        this.isSubmitting = true;
        this.errorMessage = null;

        try {

            if (this.mode === 'OrderQuestion') {

                await this.apiClient.speakerRequest.createOrderQuestion(this.activityId);

            } else {

                if (this.agendaItemId) {

                    await this.apiClient.speakerRequest.createAgendaItemSpeech(this.activityId, this.agendaItemId, this.proposal);
                }
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).$(`#${this.modalName}`).modal('hide');

            this.$emit('submitted');

        } catch (e: any) {

            if (e?.message === 'SpeakerAlreadyOnSpeakerList') {
                if (this.mode === 'OrderQuestion') {
                    this.errorMessage = this.t('speakerRequest_error_orderQuestionAlreadyExist');
                } else {
                    this.errorMessage = this.t('speakerRequest_error_speakerAlreadyOnList');
                }
            }
            else if (e?.message === 'SpeakerListNotOpen') {
                this.errorMessage = this.t('speakerRequest_error_debateNotOpen');
            } else {
                this.errorMessage = this.t('speakerRequest_error_generic');
            }
        } finally {
            this.isSubmitting = false;
        }
    }
}
